import React, { useState } from 'react';
import { Plus, CreditCard, Wallet, Trash2, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
interface PaymentMethod {
  id: number;
  type: string;
  brand: string;
  last4: string;
  expiry: string;
  isDefault: boolean;
}
const INITIAL_METHODS: PaymentMethod[] = [{
  id: 1,
  type: 'card',
  brand: 'VISA',
  last4: '4242',
  expiry: '12/25',
  isDefault: true
}, {
  id: 2,
  type: 'card',
  brand: 'Mastercard',
  last4: '8888',
  expiry: '09/24',
  isDefault: false
}];
const TOP_UP_AMOUNTS = [5000, 10000, 20000, 50000];
export function PaymentMethodsScreen() {
  const {
    navigate
  } = useNavigation();
  const [methods, setMethods] = useState<PaymentMethod[]>(INITIAL_METHODS);
  const [walletBalance, setWalletBalance] = useState(90000);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [selectedTopUp, setSelectedTopUp] = useState<number | null>(null);
  const [cardToDelete, setCardToDelete] = useState<number | null>(null);
  const handleSetDefault = (id: number) => {
    setMethods(prev => prev.map(method => ({
      ...method,
      isDefault: method.id === id
    })));
  };
  const handleDelete = (id: number) => {
    setMethods(prev => prev.filter(method => method.id !== id));
    setCardToDelete(null);
  };
  const handleTopUpConfirm = () => {
    if (selectedTopUp) {
      setWalletBalance(prev => prev + selectedTopUp);
      setIsTopUpOpen(false);
      setSelectedTopUp(null);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-1011">
      <Header title="Payment Methods" data-id="element-1012" />

      <div className="p-4 space-y-4" data-id="element-1013">
        {/* Wallet Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden" data-id="element-1014">
          <div className="p-4 flex items-center justify-between" data-id="element-1015">
            <div className="flex items-center gap-3" data-id="element-1016">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-1017">
                <Wallet className="w-6 h-6 text-primary" data-id="element-1018" />
              </div>
              <div data-id="element-1019">
                <h3 className="font-bold text-gray-900" data-id="element-1020">Jummall Wallet</h3>
                <p className="text-sm text-gray-500" data-id="element-1021">
                  Balance:{' '}
                  <span className="font-bold text-primary" data-id="element-1022">
                    ₦{walletBalance.toLocaleString()}
                  </span>
                </p>
              </div>
            </div>
            <button onClick={() => setIsTopUpOpen(!isTopUpOpen)} className="text-sm font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-lg flex items-center gap-1" data-id="element-1023">
              Top Up{' '}
              {isTopUpOpen ? <ChevronUp className="w-4 h-4" data-id="element-1024" /> : <ChevronDown className="w-4 h-4" data-id="element-1025" />}
            </button>
          </div>

          {/* Expandable Top Up Area */}
          <AnimatePresence data-id="element-1026">
            {isTopUpOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-gray-100 bg-gray-50/50 px-4 py-4" data-id="element-1027">
                <p className="text-sm font-medium text-gray-700 mb-3" data-id="element-1028">
                  Select amount to top up:
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-1029">
                  {TOP_UP_AMOUNTS.map(amount => <button key={amount} onClick={() => setSelectedTopUp(amount)} className={`py-2 rounded-xl text-sm font-bold border transition-colors ${selectedTopUp === amount ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200 hover:border-primary/50'}`} data-id="element-1030">
                      ₦{amount.toLocaleString()}
                    </button>)}
                </div>
                <Button fullWidth disabled={!selectedTopUp} onClick={handleTopUpConfirm} className="shadow-sm" data-id="element-1031">
                  Confirm Top Up
                </Button>
              </motion.div>}
          </AnimatePresence>
        </div>

        <h3 className="font-bold text-gray-900 mt-6 mb-2" data-id="element-1032">Saved Cards</h3>

        {/* Cards List */}
        <AnimatePresence mode="popLayout" data-id="element-1033">
          {methods.length > 0 ? methods.map(method => <motion.div key={method.id} layout initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95,
          transition: {
            duration: 0.2
          }
        }} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50 flex items-center justify-between overflow-hidden relative" data-id="element-1034">
                {cardToDelete === method.id ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="absolute inset-0 bg-error/5 backdrop-blur-sm flex items-center justify-between px-4 z-10" data-id="element-1035">
                    <span className="text-sm font-bold text-error" data-id="element-1036">
                      Delete this card?
                    </span>
                    <div className="flex gap-2" data-id="element-1037">
                      <button onClick={() => setCardToDelete(null)} className="p-2 bg-white rounded-lg text-gray-500 shadow-sm border border-gray-100" data-id="element-1038">
                        <X className="w-5 h-5" data-id="element-1039" />
                      </button>
                      <button onClick={() => handleDelete(method.id)} className="p-2 bg-error rounded-lg text-white shadow-sm" data-id="element-1040">
                        <Check className="w-5 h-5" data-id="element-1041" />
                      </button>
                    </div>
                  </motion.div> : null}

                <div className="flex items-center gap-4" data-id="element-1042">
                  <div className="w-12 h-8 bg-primary-800 rounded flex items-center justify-center text-[10px] text-white font-bold italic" data-id="element-1043">
                    {method.brand}
                  </div>
                  <div data-id="element-1044">
                    <p className="font-medium text-gray-900" data-id="element-1045">
                      •••• •••• •••• {method.last4}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5" data-id="element-1046">
                      <p className="text-xs text-gray-500" data-id="element-1047">
                        Expires {method.expiry}
                      </p>
                      {method.isDefault ? <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-1048">
                          DEFAULT
                        </span> : <button onClick={() => handleSetDefault(method.id)} className="text-[10px] font-bold text-primary hover:underline" data-id="element-1049">
                          Set Default
                        </button>}
                    </div>
                  </div>
                </div>
                <button onClick={() => setCardToDelete(method.id)} className="p-2 text-gray-400 hover:text-error hover:bg-error/10 rounded-lg transition-colors" data-id="element-1050">
                  <Trash2 className="w-5 h-5" data-id="element-1051" />
                </button>
              </motion.div>) : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-50 flex flex-col items-center text-center" data-id="element-1052">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3" data-id="element-1053">
                <CreditCard className="w-8 h-8 text-gray-400" data-id="element-1054" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1" data-id="element-1055">No saved cards</h4>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1056">
                Add a card for faster checkout
              </p>
              <Button variant="outline" size="sm" onClick={() => navigate(Screen.ADD_CARD as any)} data-id="element-1057">
                Add your first card
              </Button>
            </motion.div>}
        </AnimatePresence>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-1058">
        <div className="max-w-md mx-auto" data-id="element-1059">
          <Button fullWidth icon={<Plus className="w-5 h-5" data-id="element-1061" />} onClick={() => navigate(Screen.ADD_CARD as any)} className="shadow-lg shadow-primary/20" data-id="element-1060">
            Add New Card
          </Button>
        </div>
      </div>
    </motion.div>;
}